import React, { useState, useEffect } from "react";
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useApi } from "../context/ApiContext";
import { useData } from "../context/DataContext";

const PUBLISH_STEPS = [
	{
		id: "validation",
		title: "Validation",
		description: "Validating your app configuration and requirements",
		icon: "checkmark-circle-outline",
	},
	{
		id: "preparation",
		title: "Build Preparation",
		description: "Preparing your app for upload",
		icon: "construct-outline",
	},
	{
		id: "submission",
		title: "TestFlight Upload",
		description: "Uploading your app to TestFlight",
		icon: "cloud-upload-outline",
		dynamicStates: {
			queue: {
				title: "In Build Queue...",
				description: "Your app is queued for building",
			},
			provisioning: {
				title: "Setting Up Build...",
				description: "Preparing build environment",
			},
			building: {
				title: "Building App...",
				description: "Compiling your app",
			},
			uploading: {
				title: "Uploading to TestFlight...",
				description: "Uploading to TestFlight for testing",
			},
			done: {
				title: "Upload Complete",
				description: "Your app has been uploaded to TestFlight successfully",
			},
		},
	},
];

const PublishModal = ({ isVisible, onClose, appId, appName }) => {
	const { api } = useApi();
	const { updateAppData } = useData();
	const [currentStep, setCurrentStep] = useState(0);
	const [stepStatuses, setStepStatuses] = useState({
		validation: "pending",
		preparation: "pending",
		submission: "pending",
	});
	const [buildId, setBuildId] = useState(null);
	const [error, setError] = useState(null);
	const [isPublishing, setIsPublishing] = useState(false);

	// Check for existing build on mount
	useEffect(() => {
		const checkExistingBuild = async () => {
			try {
				// Check for any in-progress builds
				const response = await api(`/app/${appId}`, {
					method: "GET",
				});

				if (response.latestBuild) {
					setBuildId(response.latestBuild.id);
					// If build exists, check its current status
					const statusResponse = await api(`/app`, {
						method: "POST",
						body: {
							action: "status",
							buildId: response.latestBuild.id,
						},
					});

					updateStepBasedOnBuildStatus(statusResponse.status);
					if (["queue", "provisioning", "building", "uploading"].includes(statusResponse.status)) {
						setIsPublishing(true);
					}
				}
			} catch (err) {
				console.error("Error checking existing build:", err);
			}
		};

		if (isVisible) {
			checkExistingBuild();
		}
	});

	// Poll for build status if we have a buildId

	const updateStepBasedOnBuildStatus = (status) => {
		switch (status) {
			case "queue":
				setStepStatuses({
					validation: "success",
					preparation: "success",
					submission: "processing",
				});
				break;
			case "provisioning":
			case "building":
			case "uploading":
				setStepStatuses({
					validation: "success",
					preparation: "success",
					submission: "processing",
				});
				break;
			case "done":
				setStepStatuses({
					validation: "success",
					preparation: "success",
					submission: "success",
				});
				setIsPublishing(false);
				break;
			default:
				if (status.includes("error")) {
					setStepStatuses({
						validation: "success",
						preparation: "success",
						submission: "error",
					});
					setError("Build failed");
					setIsPublishing(false);
				}
		}
	};

	const handlePublish = async () => {
		try {
			setIsPublishing(true);
			setError(null);

			// Step 1: Validation
			setStepStatuses({ ...stepStatuses, validation: "processing" });
			const validationResponse = await api(`/app`, {
				method: "POST",
				body: {
					action: "validate_publish",
					projectId: appId,
				},
			});
			if (!validationResponse.valid) {
				setStepStatuses({
					...stepStatuses,
					validation: "error",
					preparation: "pending",
					submission: "pending",
				});
				setError(
					<View>
						<Text style={styles.errorText}>{validationResponse.message}</Text>
						{validationResponse.steps && (
							<View style={styles.manualSteps}>
								{validationResponse.steps.map((step, index) => (
									<Text key={index} style={styles.manualStep}>
										{step}
									</Text>
								))}
								{validationResponse.helpLink && (
									<TouchableOpacity style={styles.helpButton} onPress={() => window.open(validationResponse.helpLink, "_blank")}>
										<Text style={styles.helpButtonText}>View Documentation</Text>
									</TouchableOpacity>
								)}
							</View>
						)}
					</View>
				);
				return;
			}
			setStepStatuses({ ...stepStatuses, validation: "success", preparation: "pending", submission: "pending" });

			// Step 2: Build Preparation
			setStepStatuses({ ...stepStatuses, validation: "success", preparation: "processing", submission: "pending" });

			// Step 3: Submit build request
			const buildResponse = await api(`/app`, {
				method: "POST",
				body: {
					action: "build",
					projectId: appId,
				},
			});

			if (buildResponse.buildId) {
				setBuildId(buildResponse.buildId);
				setStepStatuses({
					validation: "success",
					preparation: "success",
					submission: "processing",
				});

				// Update local appData immediately
				updateAppData({
					buildStatus: "queue",
					buildDate: new Date().toISOString(),
					buildVersion: buildResponse.buildVersion || null,
				});

				// Close modal after brief delay to show success
				setTimeout(() => {
					onClose();
				}, 1000);
			} else {
				setStepStatuses({
					validation: "success",
					preparation: "success",
					submission: "error",
				});
				throw new Error(buildResponse.message || "Build submission failed");
			}
		} catch (err) {
			console.error("Error uploading app:", err);
			setError(err.message);

			// Don't change any successful steps to error
			if (!stepStatuses.validation === "success") {
				setStepStatuses((prev) => ({
					...prev,
					validation: "error",
					preparation: "pending",
					submission: "pending",
				}));
			} else if (!stepStatuses.preparation === "success") {
				setStepStatuses((prev) => ({
					...prev,
					preparation: "error",
					submission: "pending",
				}));
			} else {
				setStepStatuses((prev) => ({
					...prev,
					submission: "error",
				}));
			}
			setIsPublishing(false);
		}
	};

	if (!isVisible) return null;

	const getStepContent = (step, index) => {
		if (step.id === "submission" && stepStatuses.submission === "processing" && buildId) {
			const dynamicState = step.dynamicStates[buildId.status] || {
				title: "Processing...",
				description: "Processing your submission",
			};
			return {
				title: dynamicState.title,
				description: dynamicState.description,
			};
		}
		if (step.id === "submission" && stepStatuses.submission === "error") {
			return {
				title: "Build Failed",
				description: error || "An error occurred during the build process",
			};
		}
		return {
			title: step.title,
			description: step.description,
		};
	};

	return (
		<View style={styles.modalOverlay}>
			<View style={styles.modal}>
				<View style={styles.modalHeader}>
					<Text style={styles.modalTitle}>Upload to TestFlight</Text>
					<TouchableOpacity onPress={onClose}>
						<Ionicons name="close" size={24} color="#666" />
					</TouchableOpacity>
				</View>

				<View style={styles.modalContent}>
					<View style={styles.steps}>
						{PUBLISH_STEPS.map((step, index) => {
							const content = getStepContent(step, index);
							const status = stepStatuses[step.id];
							return (
								<View key={step.id} style={styles.step}>
									<View style={[styles.stepIcon, status === "pending" && styles.stepIconPending, status === "processing" && styles.stepIconProcessing, status === "success" && styles.stepIconSuccess, status === "error" && styles.stepIconError]}>
										<Ionicons name={status === "error" ? "alert-circle" : step.icon} size={24} color={status === "pending" ? "#666" : "#fff"} />
									</View>
									<View style={styles.stepInfo}>
										<Text style={[styles.stepTitle, status === "error" && styles.stepTitleError]}>{content.title}</Text>
										<Text style={[styles.stepDescription, status === "error" && styles.stepDescriptionError]}>{content.description}</Text>
									</View>
									{status === "processing" && <ActivityIndicator size="small" color="#0A84FF" />}
								</View>
							);
						})}
					</View>

					{error && (
						<View style={styles.errorContainer}>
							<Ionicons name="alert-circle" size={20} color="#FF453A" />
							<Text style={styles.errorText}>{error}</Text>
						</View>
					)}

					<View style={styles.modalActions}>
						<TouchableOpacity style={styles.cancelButton} onPress={onClose}>
							<Text style={styles.cancelButtonText}>Cancel</Text>
						</TouchableOpacity>
						{!isPublishing && !buildId && (
							<TouchableOpacity style={styles.publishButton} onPress={handlePublish}>
								<Text style={styles.publishButtonText}>Start Upload</Text>
							</TouchableOpacity>
						)}
						{stepStatuses.validation === "error" && (
							<TouchableOpacity
								style={styles.retryButton}
								onPress={() => {
									setBuildId(null);
									setError(null);
									setStepStatuses({
										validation: "pending",
										preparation: "pending",
										submission: "pending",
									});
									handlePublish();
								}}>
								<Text style={styles.retryButtonText}>Retry Build</Text>
							</TouchableOpacity>
						)}
						{stepStatuses.submission === "success" && (
							<TouchableOpacity style={styles.successButton} onPress={onClose}>
								<Text style={styles.successButtonText}>Done</Text>
							</TouchableOpacity>
						)}
					</View>
				</View>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	modalOverlay: {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: "rgba(0, 0, 0, 0.5)",
		justifyContent: "center",
		alignItems: "center",
		zIndex: 1000,
	},
	modal: {
		backgroundColor: "#2A2A2A",
		borderRadius: 12,
		width: "60%",
		maxWidth: 600,
	},
	modalHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		padding: 20,
		borderBottomWidth: 1,
		borderBottomColor: "#333",
	},
	modalTitle: {
		fontSize: 20,
		fontWeight: "600",
		color: "#fff",
	},
	modalContent: {
		padding: 20,
	},
	steps: {
		gap: 20,
		marginBottom: 24,
	},
	step: {
		flexDirection: "row",
		alignItems: "center",
		gap: 16,
	},
	stepIcon: {
		width: 40,
		height: 40,
		borderRadius: 20,
		backgroundColor: "#333",
		justifyContent: "center",
		alignItems: "center",
	},
	stepIconPending: {
		backgroundColor: "#333",
	},
	stepIconProcessing: {
		backgroundColor: "#0A84FF",
	},
	stepIconSuccess: {
		backgroundColor: "#34C759",
	},
	stepIconError: {
		backgroundColor: "#FF453A",
	},
	stepInfo: {
		flex: 1,
	},
	stepTitle: {
		fontSize: 16,
		fontWeight: "600",
		color: "#fff",
		marginBottom: 4,
	},
	stepDescription: {
		fontSize: 14,
		color: "#666",
	},
	errorContainer: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
		padding: 12,
		backgroundColor: "rgba(255, 69, 58, 0.1)",
		borderRadius: 8,
		marginBottom: 20,
	},
	errorText: {
		color: "#FF453A",
		fontSize: 14,
	},
	modalActions: {
		flexDirection: "row",
		justifyContent: "flex-end",
		gap: 12,
	},
	cancelButton: {
		paddingHorizontal: 16,
		paddingVertical: 8,
		borderRadius: 8,
		backgroundColor: "#333",
	},
	cancelButtonText: {
		color: "#fff",
		fontSize: 14,
	},
	publishButton: {
		paddingHorizontal: 16,
		paddingVertical: 8,
		borderRadius: 8,
		backgroundColor: "#0A84FF",
	},
	publishButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "600",
	},
	successButton: {
		paddingHorizontal: 16,
		paddingVertical: 8,
		borderRadius: 8,
		backgroundColor: "#34C759",
	},
	successButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "600",
	},
	stepTitleError: {
		color: "#FF453A",
	},
	stepDescriptionError: {
		color: "#FF453A",
	},
	manualSteps: {
		marginTop: 12,
		padding: 12,
		backgroundColor: "rgba(255, 69, 58, 0.05)",
		borderRadius: 8,
		gap: 8,
	},
	manualStep: {
		color: "#FF453A",
		fontSize: 14,
		lineHeight: 20,
	},
	helpButton: {
		marginTop: 8,
		paddingHorizontal: 16,
		paddingVertical: 8,
		backgroundColor: "#FF453A",
		borderRadius: 8,
		alignSelf: "flex-start",
	},
	helpButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "600",
	},
	retryButton: {
		paddingHorizontal: 16,
		paddingVertical: 8,
		borderRadius: 8,
		backgroundColor: "#FF9500",
	},
	retryButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "600",
	},
});

export default PublishModal;
